import { ErrorResponse, ServiceResponse, ZipCode } from '@bzbs/react-api-client';
import { create } from 'zustand';
import { useBuzzebeesAppStore } from './buzzebeess-app.store';

export type ZipCodeDropDownItem = {
  name: string;
  id: string;
};

export type ZipCodeState = {
  zipCodes: ZipCode[];
  provinceDropDownItems: ZipCodeDropDownItem[];
  districtDropDownItems: ZipCodeDropDownItem[];
  subDistrictDropDownItems: ZipCodeDropDownItem[];
  selectedProvinceDropDownItem: ZipCodeDropDownItem | null;
  selectedDistrictDropDownItem: ZipCodeDropDownItem | null;
  selectedSubDistrictDropDownItem: ZipCodeDropDownItem | null;
  isLoading: boolean;
  error: ErrorResponse | null;
};

export type ZipCodeActions = {
  fetchZipCodes: (zipCode: string) => Promise<ServiceResponse<ZipCode[]>>;
  setSelectedProvinceDropDownItem: (item: ZipCodeDropDownItem | null) => void;
  setSelectedDistrictDropDownItem: (item: ZipCodeDropDownItem | null) => void;
  setSelectedSubDistrictDropDownItem: (item: ZipCodeDropDownItem | null) => void;
  clear: () => void;
};

export type ZipCodeStore = ZipCodeState & ZipCodeActions;

function getDistinctZipCodeDropDownItems(items: ZipCodeDropDownItem[]): ZipCodeDropDownItem[] {
  const uniqueItems = new Map<string, ZipCodeDropDownItem>();
  items.forEach((item) => {
    if (!uniqueItems.has(item.id)) {
      uniqueItems.set(item.id, item);
    }
  });
  return Array.from(uniqueItems.values());
}

function buildProvinceDropDownItems(zipCodes: ZipCode[]): ZipCodeDropDownItem[] {
  return getDistinctZipCodeDropDownItems(
    zipCodes.map(
      (zipCode) =>
        ({
          name: zipCode.ProvinceName,
          id: zipCode.ProvinceCode,
        }) as ZipCodeDropDownItem
    )
  );
}

function buildDistrictDropDownItems(
  zipCodes: ZipCode[],
  selectedProvinceId: string | undefined
): ZipCodeDropDownItem[] {
  return getDistinctZipCodeDropDownItems(
    zipCodes
      .filter((zipCode) => zipCode.ProvinceCode === selectedProvinceId)
      .map(
        (zipCode) =>
          ({
            name: zipCode.DistrictName,
            id: zipCode.DistrictCode,
          }) as ZipCodeDropDownItem
      )
  );
}

function buildSubDistrictDropDownItems(
  zipCodes: ZipCode[],
  selectedDistrictId: string | undefined
): ZipCodeDropDownItem[] {
  return getDistinctZipCodeDropDownItems(
    zipCodes
      .filter((zipCode) => zipCode.DistrictCode === selectedDistrictId)
      .map(
        (zipCode) =>
          ({
            name: zipCode.SubDistrictName,
            id: zipCode.SubDistrictCode,
          }) as ZipCodeDropDownItem
      )
  );
}

export const useZipCodeStore = create<ZipCodeStore>((set, get) => ({
  zipCodes: [],
  provinceDropDownItems: [],
  districtDropDownItems: [],
  subDistrictDropDownItems: [],
  selectedProvinceDropDownItem: null,
  selectedDistrictDropDownItem: null,
  selectedSubDistrictDropDownItem: null,
  isLoading: false,
  error: null,

  fetchZipCodes: async (zipCode: string) => {
    const bzbsService = useBuzzebeesAppStore.getState().bzbsService;

    if (!bzbsService) {
      return { type: 'client-error', message: 'Service not configured' } as ServiceResponse<ZipCode[]>;
    }

    set({ isLoading: true });
    const response = await bzbsService.addressApi.zipCodes({ zipCode });
    set({ isLoading: false });

    if (response.type === 'success') {
      const zipCodes = response.model;
      set({
        zipCodes,
        provinceDropDownItems: buildProvinceDropDownItems(zipCodes),
        districtDropDownItems: [],
        subDistrictDropDownItems: [],
        selectedProvinceDropDownItem: null,
        selectedDistrictDropDownItem: null,
        selectedSubDistrictDropDownItem: null,
        error: null,
      });
    } else {
      set({ error: response });
    }

    return response;
  },

  setSelectedProvinceDropDownItem: (item: ZipCodeDropDownItem | null) => {
    const { zipCodes } = get();
    set({
      selectedProvinceDropDownItem: item,
      selectedDistrictDropDownItem: null,
      selectedSubDistrictDropDownItem: null,
      districtDropDownItems: buildDistrictDropDownItems(zipCodes, item?.id),
      subDistrictDropDownItems: [],
    });
  },

  setSelectedDistrictDropDownItem: (item: ZipCodeDropDownItem | null) => {
    const { zipCodes } = get();
    set({
      selectedDistrictDropDownItem: item,
      selectedSubDistrictDropDownItem: null,
      subDistrictDropDownItems: buildSubDistrictDropDownItems(zipCodes, item?.id),
    });
  },

  setSelectedSubDistrictDropDownItem: (item: ZipCodeDropDownItem | null) =>
    set({ selectedSubDistrictDropDownItem: item }),

  clear: () => {
    set({
      zipCodes: [],
      provinceDropDownItems: [],
      districtDropDownItems: [],
      subDistrictDropDownItems: [],
      selectedProvinceDropDownItem: null,
      selectedDistrictDropDownItem: null,
      selectedSubDistrictDropDownItem: null,
      isLoading: false,
      error: null,
    });
  },
}));
